import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Slide, 
  Link, 
  IconButton 
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Button from './Button';

// Cookie functions
const setCookieConsent = (accepted) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + 365 * 24 * 60 * 60 * 1000); // 1 year
  document.cookie = `cookieConsent=${accepted ? 'accepted' : 'declined'};expires=${expires.toUTCString()};path=/`;
};

const getCookieConsent = () => {
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i].trim();
    if (cookie.startsWith('cookieConsent=')) {
      return cookie.substring('cookieConsent='.length);
    }
  }
  return null;
};

// Custom hook to manage cookie consent
export const useCookieConsent = () => {
  // Default to false (not allowed)
  const [cookiesAllowed, setCookiesAllowed] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);
  
  useEffect(() => {
    // Check if user has already made a choice
    const consent = getCookieConsent();
    if (consent === 'accepted') {
      setCookiesAllowed(true);
    } else if (consent === 'declined') {
      setCookiesAllowed(false);
    } else {
      // Show the cookie banner after a short delay if no choice has been made
      const timer = setTimeout(() => {
        setBannerOpen(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    setCookieConsent(true);
    setCookiesAllowed(true);
    setBannerOpen(false);
  };

  const handleDecline = () => {
    setCookieConsent(false);
    setCookiesAllowed(false);
    setBannerOpen(false);
  };

  const handleClose = () => {
    setBannerOpen(false);
  };

  const CookieBanner = () => (
    <Slide direction="up" in={bannerOpen} mountOnEnter unmountOnExit>
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          bottom: 16,
          left: {xs:'5vw',md:16},
          right: 'auto',
          zIndex: 9999,
          p: 2,
          borderRadius: 2,
          width: {xs:'90vw',md:'auto'},
          maxWidth:{xs:'90vw',md:600},
          maxHeight: '70vh',
          mx: 'auto',
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          border: '1px solid rgba(0,0,0,0.08)',
          background: 'rgba(255, 255, 255, 0.67)',
          backdropFilter: 'blur(10px)',
          overflow:'auto',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
              We value your privacy
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              We use cookies to enhance your browsing experience, serve personalized ads or content, 
              and analyze our traffic. By clicking "Accept", you consent to our use of cookies. 
              Your chat history and preferences will be stored to provide a better experience.
              <Link 
                href="https://www.mymortgageline.ca/privacy-policy-terms-disclaimer/" 
                target="_blank" 
                sx={{ ml: 0.5, fontWeight: 500 }}
              >
                View our Privacy Policy & Terms
              </Link>

            </Typography>
          </Box>
          <IconButton 
            aria-label="close" 
            onClick={handleClose}
            size="small"
            sx={{ ml: 1, mt: -1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, justifyContent: {xs: 'center', sm: 'flex-end'} }}>
          <Button 
            variant="outlined" 
            onClick={handleDecline}
            sx={{ 
              px: 3,
              borderRadius: '8px',
              color: 'text.primary',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'rgba(0, 0, 0, 0.02)'
              }
            }}
          >
            Decline
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAccept}
            sx={{ 
              px: 3,
              borderRadius: '8px',
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark'
              }
            }}
          >
            Accept
          </Button>
        </Box>
      </Paper>
    </Slide>
  );

  return {
    cookiesAllowed,
    CookieBanner
  };
};

const CookiePermission = () => {
  const { cookiesAllowed, CookieBanner } = useCookieConsent();
  return <CookieBanner />;
};

export default CookiePermission;